<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useGetStartedPlatformProgress } from '../composables/useGetStartedPlatformProgress'
import type { GetStartedPlatformProgressItem } from '../models/getStartedProgress.types'

import { platformLogoProps } from './platformLogoProps'

import { useWorkspace } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { Button, Icon, PlatformLogo } from '@/shared/ui'

const progress = useGetStartedPlatformProgress()
const workspace = useWorkspace()
const { t } = useI18n()
const expanded = ref<Record<string, boolean>>({})
const previousStates = new Map<string, GetStartedPlatformProgressItem['state']>()
const workspaceName = computed(
  () => workspace.workspace.value?.name || t('integrations.progress.workspace'),
)

watch(
  progress.platforms,
  (platforms) => {
    const next = { ...expanded.value }
    for (const platform of platforms) {
      const previous = previousStates.get(platform.id)
      if (!(platform.id in next)) next[platform.id] = platform.state !== 'ready'
      else if (platform.state === 'ready' && previous !== 'ready') next[platform.id] = false
      else if (previous === 'ready' && platform.state !== 'ready') next[platform.id] = true
      previousStates.set(platform.id, platform.state)
    }
    expanded.value = next
  },
  { immediate: true },
)

function displayName(platform: GetStartedPlatformProgressItem): string {
  return platform.nameKey ? t(platform.nameKey) : platform.name
}

function toggle(platform: GetStartedPlatformProgressItem): void {
  expanded.value = { ...expanded.value, [platform.id]: !expanded.value[platform.id] }
}

function stepState(platform: GetStartedPlatformProgressItem, index: number): string {
  if (index < platform.activeStage) return 'completed'
  if (index === platform.activeStage) return 'active'
  return 'pending'
}

function completedStepDelay(platform: GetStartedPlatformProgressItem, index: number) {
  return stepState(platform, index) === 'completed'
    ? { animationDelay: `${index * 90}ms` }
    : undefined
}
</script>

<template>
  <aside
    class="get-started-progress min-w-0 p-lg border-l border-l-border bg-surface rounded-xl text-fg font-sans max-[900px]:border-t max-[900px]:border-t-border max-[900px]:border-l-0"
    aria-live="polite"
    :aria-label="t('integrations.progress.ariaLabel')"
    data-testid="get-started-platform-progress"
  >
    <header class="get-started-progress__header">
      <h2>{{ t('integrations.progress.title', { workspace: workspaceName }) }}</h2>
      <p>{{ t('integrations.progress.subtitle') }}</p>
    </header>

    <div class="get-started-progress__list grid gap-sm mt-md">
      <p v-if="progress.loading.value && progress.platforms.value.length === 0" role="status">
        {{ t('integrations.progress.loading') }}
      </p>
      <div v-else-if="progress.error.value && progress.platforms.value.length === 0" role="alert">
        <p>{{ t('integrations.progress.loadFailed') }}</p>
        <Button size="sm" variant="outline" @click="progress.refresh">
          {{ t('integrations.progress.tryAgain') }}
        </Button>
      </div>
      <p v-else-if="progress.platforms.value.length === 0">
        {{ t('integrations.progress.empty') }}
      </p>

      <article
        v-for="platform in progress.platforms.value"
        :key="platform.id"
        class="platform-progress rounded-lg border p-sm"
        :class="
          platform.state === 'ready'
            ? 'border-get-started-done/15 bg-get-started-done-soft'
            : 'border-border bg-surface'
        "
        :data-state="platform.state"
        :aria-label="`${displayName(platform)}: ${t(`integrations.progress.states.${platform.state}`)}`"
      >
        <div class="platform-progress__summary flex min-w-0 items-center gap-sm">
          <PlatformLogo
            v-if="platform.logoKey"
            v-bind="platformLogoProps(platform.logoKey, 20, displayName(platform))"
          />
          <span
            class="platform-progress__name min-w-0 flex-1 overflow-hidden font-semibold text-ellipsis whitespace-nowrap"
            >{{ displayName(platform) }}</span
          >
          <span
            class="platform-progress__status inline-flex items-center gap-xs text-[0.75rem] text-muted"
            :data-state="platform.state"
          >
            <Icon v-if="platform.state === 'ready'" name="check" :size="11" aria-hidden="true" />
            {{ t(`integrations.progress.states.${platform.state}`) }}
          </span>
          <button
            v-if="platform.stages.length > 0"
            class="platform-progress__toggle grid w-7 h-7 place-items-center border-0 rounded-full bg-transparent text-muted cursor-pointer hover:bg-get-started-hover [&:focus-visible]:outline-[length:var(--ui-ring-width)] [&:focus-visible]:outline-solid [&:focus-visible]:outline-ring"
            type="button"
            :aria-expanded="Boolean(expanded[platform.id])"
            :aria-controls="`platform-progress-steps-${platform.id}`"
            :aria-label="
              t(
                expanded[platform.id]
                  ? 'integrations.progress.hideSteps'
                  : 'integrations.progress.showSteps',
              )
            "
            @click="toggle(platform)"
          >
            <Icon
              v-if="platform.state === 'ready'"
              name="chevron-down"
              :size="11"
              aria-hidden="true"
            />
          </button>
          <Button
            v-if="platform.state === 'error' && platform.retryCommand"
            size="xs"
            variant="ghost"
            :loading="progress.retryingIds.value.has(platform.id)"
            :disabled="progress.retryingIds.value.has(platform.id)"
            @click="progress.retry(platform)"
          >
            {{ t('integrations.progress.tryAgain') }}
          </Button>
        </div>

        <p
          v-if="platform.state === 'error' && platform.errorMessage"
          class="platform-progress__error text-danger mt-xs mx-0 mb-0 text-[0.75rem]"
        >
          {{ platform.errorMessage }}
        </p>

        <Transition name="platform-steps">
          <ol
            v-if="platform.stages.length > 0 && expanded[platform.id]"
            :id="`platform-progress-steps-${platform.id}`"
            class="platform-progress__steps mt-sm mb-0 grid list-none p-0"
            :aria-label="t('integrations.progress.stepsLabel', { platform: displayName(platform) })"
          >
            <li
              v-for="(stage, index) in platform.stages"
              :key="stage"
              class="relative flex min-h-7 items-center gap-sm text-xs"
              :data-step-state="stepState(platform, index)"
              :aria-current="index === platform.activeStage ? 'step' : undefined"
            >
              <span
                v-if="index < platform.stages.length - 1"
                class="platform-progress__connector absolute left-2.5 top-4.5 h-6 w-px origin-top"
                :class="
                  stepState(platform, index) === 'completed'
                    ? 'platform-progress__connector--completed bg-get-started-done/30'
                    : 'bg-border'
                "
                :style="completedStepDelay(platform, index)"
                aria-hidden="true"
              />
              <span
                class="platform-progress__marker relative z-raised grid size-5 shrink-0 place-items-center rounded-full border-2"
                :class="{
                  'platform-progress__marker--completed border-get-started-done bg-get-started-done text-surface':
                    stepState(platform, index) === 'completed',
                  'border-primary bg-surface': stepState(platform, index) === 'active',
                  'border-border bg-surface': stepState(platform, index) === 'pending',
                }"
                :style="completedStepDelay(platform, index)"
                aria-hidden="true"
              >
                <Icon
                  v-if="stepState(platform, index) === 'completed'"
                  name="check"
                  :size="9"
                  class="platform-progress__step-check"
                />
                <span
                  v-else-if="stepState(platform, index) === 'active'"
                  class="size-2 animate-pulse rounded-full bg-primary motion-reduce:animate-none"
                />
              </span>
              <span>{{ t(`integrations.progress.stages.${stage}`) }}</span>
            </li>
          </ol>
        </Transition>
      </article>
    </div>
  </aside>
</template>

<style scoped>
.get-started-progress .get-started-progress__header h2 {
  margin: 0;
  font-size: 1.125rem;
}
.get-started-progress .get-started-progress__header p {
  margin-block: var(--ui-space-xs);
  margin-inline: 0;
  color: var(--ui-color-muted);
}
.get-started-progress .get-started-progress__list > p {
  margin-block: var(--ui-space-xs);
  margin-inline: 0;
  color: var(--ui-color-muted);
}
.get-started-progress .platform-progress__toggle span {
  transition-property: transform;
  transition-timing-function: ease;
  transition-duration: 180ms;
}
.get-started-progress .platform-progress__toggle[aria-expanded='true'] span {
  transform: rotate(180deg);
}
.get-started-progress .platform-progress__steps li {
  display: flex;
  align-items: center;
  gap: var(--ui-space-xs);
  color: var(--ui-color-muted);
  font-size: 0.75rem;
}
.get-started-progress .platform-progress__steps li[data-step-state='active'] {
  color: var(--ui-color-primary);
  font-weight: 600;
}
.get-started-progress .platform-progress__steps li[data-step-state='completed'] {
  color: var(--ui-color-get-started-done);
}

.platform-progress__marker--completed {
  animation: platform-step-complete 360ms cubic-bezier(0.22, 1, 0.36, 1) both;
}
.platform-progress__step-check {
  animation: platform-step-check 260ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: inherit;
}
.platform-progress__connector--completed {
  animation: platform-step-connector 360ms ease-out both;
  animation-delay: inherit;
}
.platform-steps-enter-active,
.platform-steps-leave-active {
  overflow: hidden;
  transition:
    max-height 220ms ease,
    opacity 180ms ease,
    margin-top 220ms ease;
}
.platform-steps-enter-from,
.platform-steps-leave-to {
  max-height: 0;
  margin-top: 0;
  opacity: 0;
}
.platform-steps-enter-to,
.platform-steps-leave-from {
  max-height: 10rem;
  opacity: 1;
}
@keyframes platform-step-complete {
  from {
    opacity: 0.4;
    transform: scale(0.72);
  }
  70% {
    transform: scale(1.06);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
@keyframes platform-step-check {
  from {
    opacity: 0;
    transform: rotate(-25deg) scale(0.3);
  }
  to {
    opacity: 1;
    transform: rotate(0) scale(1);
  }
}
@keyframes platform-step-connector {
  from {
    transform: scaleY(0);
  }
  to {
    transform: scaleY(1);
  }
}
@media (prefers-reduced-motion: reduce) {
  .get-started-progress .platform-progress__toggle span {
    transition-property: none;
  }
  .platform-progress__marker--completed,
  .platform-progress__step-check,
  .platform-progress__connector--completed {
    animation: none;
  }
}

.platform-progress__status[data-state='ready'] {
  color: var(--ui-color-get-started-done);
}
.platform-progress__status[data-state='error'] {
  color: var(--ui-color-danger);
}
</style>
